import axios from '@/lib/axios'
import { buildAdminRequestConfig, type OrganizationId } from '@/lib/organization-scoping'
import type {
  SuperTicketLayoutInput,
  SuperTicketPreview,
  SuperTicketTemplate,
  SuperTicketTemplateSettings,
} from './super-ticket-settings.types'

export interface SuperTicketOrganizationScope {
  organizationId: OrganizationId
}

/** Scope for calls on one template; its Laravel routes require the template. */
export interface SuperTicketTemplateScope extends SuperTicketOrganizationScope {
  template: SuperTicketTemplate
}

export interface SuperTicketPreviewInput extends SuperTicketTemplateScope {
  layout: SuperTicketLayoutInput
  /** Render this order; without one, the API renders sample data. */
  salesOrderId?: number | string | null
}

export interface SaveSuperTicketTemplateInput extends SuperTicketTemplateScope {
  /** The `version` the edits started from; a stale one answers 409. */
  version: number
  layout: SuperTicketLayoutInput
}

interface AbortableRequest {
  signal?: AbortSignal
}

export function getSuperTicketTemplates({
  organizationId,
  signal,
}: SuperTicketOrganizationScope & AbortableRequest) {
  return axios.get<SuperTicketTemplateSettings[]>(
    '/super-ticket-templates',
    buildAdminRequestConfig({ organizationId, signal })
  )
}

export function previewSuperTicket({
  organizationId,
  template,
  layout,
  salesOrderId,
  signal,
}: SuperTicketPreviewInput & AbortableRequest) {
  return axios.post<SuperTicketPreview>(
    `/super-ticket-templates/${template}/preview`,
    {
      layout,
      ...(salesOrderId ? { sales_order_id: Number(salesOrderId) } : {}),
    },
    buildAdminRequestConfig({ organizationId, signal })
  )
}

export function saveSuperTicketTemplate({
  organizationId,
  template,
  version,
  layout,
}: SaveSuperTicketTemplateInput) {
  return axios.put<SuperTicketTemplateSettings>(
    `/super-ticket-templates/${template}`,
    {
      expected_version: version,
      layout,
    },
    buildAdminRequestConfig({ organizationId })
  )
}
